import { useEffect, useState } from "react";
import { Loader2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500";

export const SponsorCheckoutModal = ({ tier, events, onClose }) => {
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", target_event: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  if (!tier) return null;
  const deposit = tier.price * 0.25;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/sponsors/checkout", { ...form, tier_id: tier.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 backdrop-blur-sm sm:items-center" onClick={onClose} data-testid="sponsor-checkout-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="glass w-full max-w-lg rounded-3xl p-6 ring-gold md:p-8">
        <div className="flex items-start justify-between">
          <div>
            <span className="eyebrow">Secure this tier</span>
            <h2 className="mt-1 font-display text-3xl uppercase text-chrome">{tier.name} <span className="text-gold">${(tier.price / 1000).toFixed(0)}K</span></h2>
          </div>
          <button type="button" onClick={onClose} data-testid="sponsor-checkout-close" className="rounded-full p-2 text-neutral-400 hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 rounded-2xl border border-cyan-500/30 bg-black/30 p-4">
          <div className="flex items-center justify-between"><span className="text-sm text-neutral-300">25% refundable deposit today</span><span className="font-display text-2xl text-gold" data-testid="sponsor-deposit-amount">${deposit.toLocaleString()}</span></div>
          <p className="mt-1 text-xs text-neutral-500">Balance of ${(tier.price - deposit).toLocaleString()} invoiced 30 days before activation. Your branded sponsor deck is generated instantly after payment.</p>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input required placeholder="Company / brand" value={form.company} onChange={set("company")} data-testid="checkout-company" className={inputCls} />
          <input required placeholder="Contact name" value={form.name} onChange={set("name")} data-testid="checkout-name" className={inputCls} />
          <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="checkout-email" className={inputCls} />
          <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="checkout-phone" className={inputCls} />
          <select value={form.target_event} onChange={set("target_event")} data-testid="checkout-event" className={`${inputCls} sm:col-span-2`}>
            <option value="">Target event (optional)</option>
            {events.map((e) => <option key={e.slug} value={e.title}>{e.title}</option>)}
            <option value="Multi-event / season">Multi-event / season</option>
          </select>
        </div>
        <button type="submit" disabled={busy} data-testid="sponsor-checkout-pay" className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-full py-4 font-display text-lg uppercase disabled:opacity-60">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay ${deposit.toLocaleString()} deposit</>}
        </button>
        <p className="mt-3 text-center font-mono-label text-[10px] uppercase tracking-widest text-stone-600">Secure checkout by Stripe</p>
      </form>
    </div>
  );
};
